<template>
  <f7-card class="package-card elevation-8">
    <!-- Enhanced Package Header with Media -->
    <f7-card-header class="package-header">
      <div class="package-header-main">
        <div class="package-icon-container">
          <f7-icon f7="cube_box_fill" size="32" color="white"></f7-icon>
        </div>
        <div class="package-title-section">
          <div class="package-title">{{ box.title }}</div>
          <f7-chip
            :text="box.code"
            outline
            class="package-code-chip"
          >
            <template #media>
              <f7-icon f7="number_square_fill" size="16"></f7-icon>
            </template>
          </f7-chip>
        </div>
      </div>
      <div class="status-badge-stack">
        <f7-badge
          :color="box.statusMeta.color"
          class="status-badge"
        >
          {{ statusText }}
        </f7-badge>
        <f7-badge
          v-if="needsMemberAction"
          color="orange"
          class="status-badge status-badge-action"
        >
          {{ $t('box.totals.actionNeeded') }}
        </f7-badge>
      </div>
    </f7-card-header>

    <!-- Package Statistics using Framework7 List -->
    <f7-card-content class="package-content">
      <f7-list
        no-hairlines
        no-hairlines-between
        class="package-stats-list"
      >
        <f7-list-item class="stat-item">
          <template #media>
            <div class="stat-icon-wrapper stat-icon-primary">
              <f7-icon f7="number_square" size="20"></f7-icon>
            </div>
          </template>
          <template #title>
            <span class="stat-label">{{ $t('box.stats.totalItems') }}</span>
          </template>
          <template #after>
            <span class="stat-value stat-value-primary">{{ box.items?.length || 0 }}</span>
          </template>
        </f7-list-item>

        <f7-list-item class="stat-item">
          <template #media>
            <div class="stat-icon-wrapper stat-icon-secondary">
              <f7-icon f7="cube_box" size="20"></f7-icon>
            </div>
          </template>
          <template #title>
            <span class="stat-label">{{ $t('box.stats.products') }}</span>
          </template>
          <template #after>
            <span class="stat-value stat-value-secondary">{{ box.totalItems }}</span>
          </template>
        </f7-list-item>

        <f7-list-item class="stat-item stat-item-highlighted">
          <template #media>
            <div class="stat-icon-wrapper stat-icon-accent">
              <f7-icon f7="money_dollar_circle" size="20"></f7-icon>
            </div>
          </template>
          <template #title>
            <span class="stat-label stat-label-emphasized">{{ $t('box.stats.total') }}</span>
          </template>
          <template #after>
            <span class="stat-value stat-value-emphasized">{{ formatCurrency(box.totalAmount) }}</span>
          </template>
        </f7-list-item>

        <!-- PoolShare reward row. Surfaces the unrealized poolshare yield on
             every box at a glance — drives "what's pending?" curiosity and
             reinforces poolshare as a primary marketing asset. Hidden when
             there's nothing to show or when the box is already CLOSED (in
             which case the reward has already landed in the wallet, so the
             "potential" framing would be misleading). -->
        <f7-list-item
          v-if="potentialPoolShareCents > 0 && !isClosed"
          class="stat-item stat-item-poolshare"
        >
          <template #media>
            <div class="stat-icon-wrapper stat-icon-poolshare">
              <f7-icon
                f7="gift_fill"
                size="20"
                color="green"
              ></f7-icon>
            </div>
          </template>
          <template #title>
            <span class="stat-label stat-label-poolshare">{{ $t('box.stats.potentialPoolShare') }}</span>
          </template>
          <template #after>
            <!-- The + comes from Intl, not from the template: a sign glued on
                 outside formatCurrency's RLM marks detaches in RTL. -->
            <span class="stat-value stat-value-poolshare">{{ formatSignedCurrency(potentialPoolShareCents) }}</span>
          </template>
        </f7-list-item>
      </f7-list>
    </f7-card-content>

    <!-- Issues Warning Banner — tappable: opens the decisions inbox (/issues/)
         instead of telling the member to fix something with nowhere to go. -->
    <f7-card-content v-if="issuesCount > 0" class="issues-banner" padding>
      <button type="button" class="issues-content" @click="handleIssuesBannerClick">
        <f7-icon f7="exclamationmark_triangle_fill" size="20" color="red"></f7-icon>
        <span class="issues-text">{{ $t('box.stats.issuesWarning', {count: issuesCount}) }}</span>
        <f7-icon f7="chevron_left" size="18" color="gray"></f7-icon>
      </button>
    </f7-card-content>

    <!-- Action Links Footer -->
    <f7-card-footer v-if="canEditShipping" class="package-footer">
      <f7-link
        icon-f7="location_fill"
        icon-size="20"
        @click="handleViewShipping"
      >
        {{ $t('box.shipping.view') }}
      </f7-link>
      <f7-link
        icon-f7="pencil_circle_fill"
        icon-size="20"
        @click="handleEditShipping"
      >
        {{ $t('box.shipping.edit') }}
      </f7-link>
    </f7-card-footer>

    <!-- Totals CTA — surfaces from the moment estimatedTotal is populated
         (always after creation). Stage-aware label + color. f7-button with
         `href` renders as an <a> and handles F7 routing — wrapping it in
         <f7-link> would nest two anchors and the inner button would swallow
         the click. -->
    <f7-card-footer v-if="canViewTotals" class="package-footer package-footer-totals">
      <f7-button
        :href="`/orders/box/${box.id}/totals/`"
        :color="totalsCtaColor"
        :fill="needsMemberAction"
        :outline="!needsMemberAction"
        small
        class="totals-cta-button"
      >
        <f7-icon
          :f7="totalsCtaIcon"
          size="18"
          class="margin-right-half"
        ></f7-icon>
        {{ totalsCtaText }}
      </f7-button>
    </f7-card-footer>

    <!-- Shipping Info Sheet (View Only) -->
    <f7-sheet
      v-model:opened="sheetShippingInfoOpened"
      swipe-to-close
      backdrop
      class="shipping-info-sheet"
    >
      <f7-toolbar>
        <div class="left" />
        <div class="center">{{ $t('box.shipping.info') }}</div>
        <div class="right">
          <f7-link sheet-close>{{ $t('general.close') }}</f7-link>
        </div>
      </f7-toolbar>
      <f7-page-content>
        <f7-block-title medium>{{ $t('box.shipping.info') }}</f7-block-title>
        <f7-list
          strong-ios
          outline-ios
          dividers-ios
          inset
          media-list
        >
          <f7-list-item
            :title="$t('box.shipping.name')"
            :after="box.shipName"
          >
            <template #media>
              <f7-icon f7="person_fill" size="24" color="purple"></f7-icon>
            </template>
          </f7-list-item>
          <f7-list-item
            :title="$t('box.shipping.phone')"
            :after="box.shipPhone"
          >
            <template #media>
              <f7-icon f7="phone_fill" size="24" color="purple"></f7-icon>
            </template>
          </f7-list-item>
          <f7-list-item
            :title="$t('box.shipping.address')"
          >
            <template #media>
              <f7-icon f7="house_fill" size="24" color="purple"></f7-icon>
            </template>
            <template #after>
              <div class="item-after-multiline">{{ box.shipAddress }}</div>
            </template>
          </f7-list-item>
          <f7-list-item
            :title="$t('box.shipping.governorate')"
            :after="getGovernorateLabel(box.shipGovernorate)"
          >
            <template #media>
              <f7-icon f7="map_fill" size="24" color="purple"></f7-icon>
            </template>
          </f7-list-item>
        </f7-list>
      </f7-page-content>
    </f7-sheet>

    <!-- Edit Shipping Sheet -->
    <f7-sheet
      v-model:opened="sheetShippingEditOpened"
      backdrop
      class="shipping-edit-sheet"
    >
      <f7-toolbar>
        <div class="left" />
        <div class="center">{{ $t('box.shipping.edit') }}</div>
        <div class="right">
          <f7-link sheet-close>{{ $t('general.close') }}</f7-link>
        </div>
      </f7-toolbar>
      <f7-page-content>
        <f7-block-title medium>{{ $t('box.shipping.info') }}</f7-block-title>
        <f7-list strong-ios outline-ios dividers-ios inset>
          <!-- Name -->
          <f7-list-input
            v-model:value="editForm.shipName"
            :label="$t('box.shipping.name') + ' *'"
            type="text"
            :placeholder="$t('box.shipping.namePlaceholder')"
            :error-message="editErrors.shipName"
            :error-message-force="!!editErrors.shipName"
            required
            validate
            outline
            floating-label
            clear-button
            @input="clearEditError('shipName')"
          >
            <template #media>
              <f7-icon f7="person_fill" color="purple"></f7-icon>
            </template>
          </f7-list-input>

          <!-- Phone with proper RTL/LTR handling -->
          <f7-list-input
            v-model:value="editForm.shipPhone"
            :label="$t('box.shipping.phone') + ' *'"
            :placeholder="$t('box.shipping.phonePlaceholder')"
            type="tel"
            required
            validate
            outline
            floating-label
            :error-message="editErrors.shipPhone"
            :error-message-force="!!editErrors.shipPhone"
            :input-style="{ direction: 'ltr', textAlign: 'left' }"
            @input="clearEditError('shipPhone')"
          >
            <template #media>
              <f7-icon f7="phone_fill" color="purple"></f7-icon>
            </template>
            <template #inner-end>
              <f7-link
                v-if="editForm.shipPhone"
                icon-f7="xmark_circle_fill"
                icon-size="20px"
                @click="editForm.shipPhone = ''; clearEditError('shipPhone')"
              ></f7-link>
            </template>
          </f7-list-input>

          <!-- Address -->
          <f7-list-input
            v-model:value="editForm.shipAddress"
            :label="$t('box.shipping.address') + ' *'"
            type="textarea"
            :placeholder="$t('box.shipping.addressPlaceholder')"
            :error-message="editErrors.shipAddress"
            :error-message-force="!!editErrors.shipAddress"
            required
            validate
            outline
            floating-label
            resizable
            @input="clearEditError('shipAddress')"
          >
            <template #media>
              <f7-icon f7="house_fill" color="purple"></f7-icon>
            </template>
          </f7-list-input>

          <!-- Governorate Smart Select -->
          <f7-list-item
            :title="$t('box.shipping.governorate') + ' *'"
            smart-select
            :smart-select-params="{ openIn: 'popover', searchbar: true, searchbarPlaceholder: $t('general.search') }"
          >
            <template #media>
              <f7-icon f7="map_fill" color="purple"></f7-icon>
            </template>
            <select v-model="editForm.shipGovernorate" @change="clearEditError('shipGovernorate')">
              <option value="">
                {{ $t('general.selectGovernorate') }}
              </option>
              <option value="cairo">
                {{ $t('governorates.cairo') }}
              </option>
              <option value="alexandria">
                {{ $t('governorates.alexandria') }}
              </option>
              <option value="giza">
                {{ $t('governorates.giza') }}
              </option>
              <option value="qalyubia">
                {{ $t('governorates.qalyubia') }}
              </option>
              <option value="sharqia">
                {{ $t('governorates.sharqia') }}
              </option>
              <option value="gharbia">
                {{ $t('governorates.gharbia') }}
              </option>
              <option value="menoufia">
                {{ $t('governorates.menoufia') }}
              </option>
              <option value="beheira">
                {{ $t('governorates.beheira') }}
              </option>
              <option value="ismailia">
                {{ $t('governorates.ismailia') }}
              </option>
              <option value="dakahlia">
                {{ $t('governorates.dakahlia') }}
              </option>
              <option value="damietta">
                {{ $t('governorates.damietta') }}
              </option>
              <option value="kafr_el_sheikh">
                {{ $t('governorates.kafr_el_sheikh') }}
              </option>
              <option value="fayoum">
                {{ $t('governorates.fayoum') }}
              </option>
              <option value="beni_suef">
                {{ $t('governorates.beni_suef') }}
              </option>
              <option value="minya">
                {{ $t('governorates.minya') }}
              </option>
              <option value="assiut">
                {{ $t('governorates.assiut') }}
              </option>
              <option value="sohag">
                {{ $t('governorates.sohag') }}
              </option>
              <option value="qena">
                {{ $t('governorates.qena') }}
              </option>
              <option value="aswan">
                {{ $t('governorates.aswan') }}
              </option>
              <option value="luxor">
                {{ $t('governorates.luxor') }}
              </option>
              <option value="red_sea">
                {{ $t('governorates.red_sea') }}
              </option>
              <option value="new_valley">
                {{ $t('governorates.new_valley') }}
              </option>
              <option value="matrouh">
                {{ $t('governorates.matrouh') }}
              </option>
              <option value="north_sinai">
                {{ $t('governorates.north_sinai') }}
              </option>
              <option value="south_sinai">
                {{ $t('governorates.south_sinai') }}
              </option>
              <option value="port_said">
                {{ $t('governorates.port_said') }}
              </option>
              <option value="suez">
                {{ $t('governorates.suez') }}
              </option>
              <option value="other">
                {{ $t('governorates.other') }}
              </option>
            </select>
          </f7-list-item>

          <!-- Show validation error for governorate if any -->
          <f7-list-item v-if="editErrors.shipGovernorate">
            <span class="text-color-red" style="font-size: 12px; padding: 0 16px;">
              {{ editErrors.shipGovernorate }}
            </span>
          </f7-list-item>
        </f7-list>

      </f7-page-content>

      <!--
        «حفظ» lives OUTSIDE the scroller. F7-Vue renders the #fixed slot as a
        direct child of .sheet-modal (framework7-vue/components/sheet.js:206-212),
        so it is a SIBLING of the scroll container. This is the form where that
        mattered most: four fields, the last of them a resizable address
        textarea, so save sat further down a longer body than anywhere else in
        this pack.

        It does NOT beat the keyboard, and nothing here claims it does: Telegram
        does not resize the WebView on iOS, so with the keyboard up this footer
        can sit beneath it until dismissed (playbook row 29). What the move buys
        is that dismissing the keyboard is now enough — no scrolling after it.

        The governorate smart-select opens a POPOVER, which F7 mounts on the app
        root rather than inside the sheet (framework7/components/modal/modal-class
        .js:22-25, 97-98) at z-index 13500 against .sheet-modal's 12500 — so it
        paints above this footer and the sheet's `overflow: hidden` cannot clip
        it. The footer's own z-index lives inside the sheet's stacking context
        and cannot reach it.
      -->
      <template #fixed>
        <div class="sheet-fixed-footer">
          <f7-button
            fill
            large
            color="purple"
            :disabled="isUpdating"
            @click="saveShipping"
          >
            <f7-preloader v-if="isUpdating" size="20" class="margin-end-half"></f7-preloader>
            <f7-icon v-else f7="checkmark_circle_fill" class="margin-end-half"></f7-icon>
            {{ isUpdating ? $t('general.loading') : $t('general.save') }}
          </f7-button>
        </div>
      </template>
    </f7-sheet>
  </f7-card>
</template>

<script setup>
import { f7 } from 'framework7-vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';
import { useIssueNavigation } from '@/composables/useIssueNavigation.js';
import { ItemStatusCategories } from '@/enums/commerce/ItemStatusConfig.js';
import { OrderBoxStatus, OrderBoxStatusMeta } from '@/enums/commerce/OrderBoxStatus.enum.js';

const props = defineProps({
  box: {
    type: Object,
    required: true,
  },
  isUpdating: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:box', 'close:box', 'delete:box']);

const { openInbox } = useIssueNavigation();
const handleIssuesBannerClick = () => openInbox();

// SHIPPED is now the member's action moment (mark delivered) → green + check.
// DELIVERED ('confirm') is passive, awaiting the admin's settle → muted + clock.
const TOTALS_CTA_COLORS = {
  shipped: 'green',
  confirm: 'gray',
  receipt: 'gray',
  draft: 'gray',
};

const TOTALS_CTA_ICONS = {
  shipped: 'checkmark_seal_fill',
  confirm: 'hourglass',
  receipt: 'doc_text_fill',
  draft: 'chart_bar_fill',
};

const { t } = useI18n();
const { formatCurrency, formatSignedCurrency } = useCurrency();

const sheetShippingInfoOpened = ref(false);
const sheetShippingEditOpened = ref(false);
const editForm = ref({
  shipName: '',
  shipPhone: '',
  shipAddress: '',
  shipGovernorate: '',
});

const editErrors = ref({
  shipName: '',
  shipPhone: '',
  shipAddress: '',
  shipGovernorate: '',
});

// Status display — derived directly from OrderBoxStatusMeta (SSOT in enum file)
const statusMeta = computed(() => OrderBoxStatusMeta[props.box.status] ?? null);
const statusText  = computed(() => statusMeta.value ? t(`${statusMeta.value.i18nKey}.label`) : '');

const canEditShipping = computed(() => {
  return [OrderBoxStatus.DRAFT, OrderBoxStatus.ACTIVE,OrderBoxStatus.READY].includes(props.box.status);
});

// Totals CTA is gated on the estimatedTotal payload, which the backend
// populates from box creation. Visible from DRAFT onward.
const canViewTotals = computed(() => props.box.estimatedTotal != null);

// The member's action is now at SHIPPED (mark "I received it"); DELIVERED is a
// passive awaiting-admin state, so it no longer flags "action needed".
const needsMemberAction = computed(() => props.box.status === OrderBoxStatus.SHIPPED);

const potentialPoolShareCents = computed(() => props.box.potentialPoolShareCents ?? 0);
const isClosed = computed(() => props.box.status === OrderBoxStatus.CLOSED);

const totalsStage = computed(() => {
  if (props.box.status === OrderBoxStatus.DELIVERED) return 'confirm';
  if (props.box.status === OrderBoxStatus.CLOSED) return 'receipt';
  if (props.box.estimatedTotal?.stage === 'shipped') return 'shipped';
  return 'draft';
});

const totalsCtaText = computed(() => t(`box.totals.cta.${totalsStage.value}`));
const totalsCtaColor = computed(() => TOTALS_CTA_COLORS[totalsStage.value] ?? 'teal');
const totalsCtaIcon = computed(() => TOTALS_CTA_ICONS[totalsStage.value] ?? 'doc_text_fill');

// Counts ONLY ship-blocking issues (ItemStatus.ISSUE). OUT_OF_STOCK is terminal
// awareness-only and must NOT trigger the "solve issues before shipping" banner.
// Reads the same SSOT as the ship-request gate (OrdersPage.getIssuesCount), so
// the banner and the gate can never disagree.
const issuesCount = computed(() => {
  if (!props.box.items) return 0;
  return props.box.items.filter(item =>
    ItemStatusCategories.BLOCKS_SHIPPING.includes(item.status),
  ).length;
});

const getGovernorateLabel = (governorateValue) => {
  if (!governorateValue) return '';
  return t(`governorates.${governorateValue}`);
};

const clearEditError = (field) => {
  editErrors.value[field] = '';
};

const validateEditForm = () => {
  let isValid = true;

  // Reset errors
  Object.keys(editErrors.value).forEach(key => {
    editErrors.value[key] = '';
  });

  // Validate shipName
  if (!editForm.value.shipName || !editForm.value.shipName.trim()) {
    editErrors.value.shipName = t('general.requiredField');
    isValid = false;
  }

  // Validate shipPhone
  if (!editForm.value.shipPhone || !editForm.value.shipPhone.trim()) {
    editErrors.value.shipPhone = t('general.requiredField');
    isValid = false;
  } else if (!/^\+?[0-9]{10,15}$/.test(editForm.value.shipPhone.replace(/\s/g, ''))) {
    editErrors.value.shipPhone = t('general.invalidPhone');
    isValid = false;
  }

  // Validate shipAddress
  if (!editForm.value.shipAddress || !editForm.value.shipAddress.trim()) {
    editErrors.value.shipAddress = t('general.requiredField');
    isValid = false;
  }

  // Validate shipGovernorate
  if (!editForm.value.shipGovernorate || !editForm.value.shipGovernorate.trim()) {
    editErrors.value.shipGovernorate = t('general.requiredField');
    isValid = false;
  }

  return isValid;
};

const handleViewShipping = () => {
  sheetShippingInfoOpened.value = true;
};

const handleEditShipping = () => {
  editForm.value = {
    shipName: props.box.shipName || '',
    shipPhone: props.box.shipPhone || '',
    shipAddress: props.box.shipAddress || '',
    shipGovernorate: props.box.shipGovernorate || '',
  };

  // Reset errors when opening edit sheet
  Object.keys(editErrors.value).forEach(key => {
    editErrors.value[key] = '';
  });

  sheetShippingEditOpened.value = true;
};

const saveShipping = () => {
  // Validate form before saving
  if (!validateEditForm()) {
    f7?.toast?.create?.({
      text: t('box.shipping.fillRequired'),
      position: 'center',
      closeTimeout: 2000,
    })?.open();
    return;
  }

  const updatedBox = {
    id:props.box.id,
    shipName: editForm.value.shipName.trim(),
    shipPhone: editForm.value.shipPhone.trim(),
    shipAddress: editForm.value.shipAddress.trim(),
    shipGovernorate: editForm.value.shipGovernorate.trim(),
  };

  emit('update:box', updatedBox);
  sheetShippingEditOpened.value = false;
};
</script>

<style scoped>
/* All existing styles remain unchanged */
.package-card {
    margin: 0;
    height: auto;
    border-radius: var(--poolit-radius-lg);
    overflow: hidden;
    box-shadow: var(--poolit-shadow-md);
    transition: transform var(--poolit-transition-normal) var(--poolit-transition-ease),
    box-shadow var(--poolit-transition-normal) var(--poolit-transition-ease);
}

.package-card:active {
    transform: scale(0.98);
}

.elevation-8 {
    box-shadow: 0 8px 24px rgba(var(--poolit-primary-rgb), 0.12),
    0 4px 8px rgba(0, 0, 0, 0.08);
}

.package-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: var(--poolit-spacing-md);
    background: linear-gradient(135deg,
    var(--poolit-primary-500) 0%,
    var(--poolit-primary-600) 100%);
    min-height: 88px;
}

.package-header-main {
    display: flex;
    align-items: center;
    gap: var(--poolit-spacing-md);
    flex: 1;
    min-width: 0;
}

.package-icon-container {
    width: 56px;
    height: 56px;
    border-radius: var(--poolit-radius-lg);
    background: rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.package-title-section {
    display: flex;
    flex-direction: column;
    gap: var(--poolit-spacing-xs);
    flex: 1;
    min-width: 0;
}

.package-title {
    font-size: 20px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-text-on-primary);
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.package-code-chip {
    align-self: flex-start;
    background: rgba(255, 255, 255, 0.25);
    color: var(--poolit-text-on-primary);
    border: 1px solid rgba(255, 255, 255, 0.3);
    font-weight: var(--poolit-font-weight-semibold);
    font-size: 13px;
    backdrop-filter: blur(8px);
}

.status-badge {
    flex-shrink: 0;
    font-size: 12px;
    font-weight: var(--poolit-font-weight-semibold);
    padding: var(--poolit-spacing-xs) var(--poolit-spacing-sm);
    border-radius: var(--poolit-radius-full);
    text-transform: uppercase;
    letter-spacing: 0.5px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.status-badge-stack {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
}

/* Quiet "action needed" badge — the orange CTA at the card footer is the
   primary attention signal. The badge is a quiet inline label that reads as
   information, not alarm. No pulse: in a list of cards, multiple DELIVERED
   boxes would strobe and read as noise. */
.status-badge-action {
    text-transform: none;
    letter-spacing: 0;
    font-size: 11px;
    box-shadow: none;
    opacity: 0.92;
}

.package-footer-totals {
    padding: var(--poolit-spacing-sm) var(--poolit-spacing-md);
    border-top: 1px solid var(--poolit-border-color);
}

.totals-cta-button {
    width: 100%;
}

.package-content {
    padding: 0;
}

.package-stats-list {
    margin: 0;
    background: transparent;
}

.package-stats-list ul {
    background: var(--poolit-bg-primary);
}

.stat-item {
    transition: background-color var(--poolit-transition-fast) var(--poolit-transition-ease);
}

.stat-item:active {
    background-color: var(--poolit-bg-soft);
}

.stat-item-highlighted {
    background: linear-gradient(90deg,
    rgba(var(--poolit-primary-rgb), 0.03) 0%,
    rgba(var(--poolit-primary-rgb), 0.06) 100%);
}

.stat-icon-wrapper {
    width: 40px;
    height: 40px;
    border-radius: var(--poolit-radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform var(--poolit-transition-fast) var(--poolit-transition-ease);
}

.stat-item:active .stat-icon-wrapper {
    transform: scale(0.95);
}

.stat-icon-primary {
    background: rgba(var(--poolit-primary-rgb), 0.1);
}

.stat-icon-secondary {
    background: rgba(var(--poolit-secondary-rgb), 0.1);
}

.stat-icon-accent {
    background: rgba(var(--poolit-accent-main-rgb), 0.1);
}

.stat-label {
    font-size: 13px;
    font-weight: var(--poolit-font-weight-medium);
    color: var(--poolit-text-tertiary);
    letter-spacing: 0.2px;
}

.stat-label-emphasized {
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-secondary);
}

.stat-value {
    font-size: 18px;
    font-weight: var(--poolit-font-weight-bold);
    color: var(--poolit-text-primary);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.stat-value-primary {
    color: var(--poolit-primary-600);
}

.stat-value-secondary {
    color: var(--poolit-secondary-600);
}

.stat-value-emphasized {
    font-size: 20px;
    color: var(--poolit-primary-600);
    font-weight: var(--poolit-font-weight-bold);
}

.stat-item-poolshare {
    background: linear-gradient(90deg,
    rgba(34, 197, 94, 0.04) 0%,
    rgba(34, 197, 94, 0.10) 100%);
}

.stat-icon-poolshare {
    background: rgba(34, 197, 94, 0.12);
}

.stat-label-poolshare {
    font-weight: var(--poolit-font-weight-semibold);
    color: var(--poolit-text-secondary);
}

.stat-value-poolshare {
    color: var(--poolit-success-600, #1b8a3b);
    font-feature-settings: 'tnum' on, 'lnum' on;
}

.issues-banner {
    background: linear-gradient(90deg,
    rgba(var(--poolit-error-rgb), 0.08) 0%,
    rgba(var(--poolit-error-rgb), 0.12) 100%);
    border-top: 1px solid rgba(var(--poolit-error-rgb), 0.2);
    border-bottom: 1px solid rgba(var(--poolit-error-rgb), 0.2);
    padding: var(--poolit-spacing-sm) var(--poolit-spacing-md);
}

.issues-content {
    display: flex;
    align-items: center;
    gap: var(--poolit-spacing-sm);
    width: 100%;
    min-height: 44px; /* WCAG target size */
    padding: 0;
    border: none;
    background: none;
    text-align: start;
    cursor: pointer;
}

.issues-content:active {
    filter: brightness(0.97);
}

.issues-content:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px rgba(var(--poolit-error-rgb), 0.4);
}

.issues-text {
    font-size: 13px;
    font-weight: var(--poolit-font-weight-medium);
    color: var(--poolit-error-600);
    line-height: 1.5;
    flex: 1;
}

.package-footer {
    display: flex;
    gap: var(--poolit-spacing-md);
    padding: var(--poolit-spacing-md);
    justify-content: center;
    background: var(--poolit-bg-soft);
    border-top: 1px solid var(--poolit-border-color);
}

.package-footer .link {
    display: flex;
    align-items: center;
    gap: var(--poolit-spacing-xs);
    font-size: 14px;
    font-weight: var(--poolit-font-weight-medium);
    color: var(--poolit-text-link);
    transition: all var(--poolit-transition-fast) var(--poolit-transition-ease);
    padding: var(--poolit-spacing-xs) var(--poolit-spacing-sm);
    border-radius: var(--poolit-radius-sm);
}

.package-footer .link:active {
    background: rgba(var(--poolit-primary-rgb), 0.08);
    transform: scale(0.97);
}

/*
  DEFINITE height on the EDIT sheet, which had no height rule at all and so took
  Framework7's default. With a definite height F7's own chain produces the
  scroller (.sheet-modal-inner 100% -> .page-content 100%; overflow:auto), which
  is what lets the save button sit outside it in the #fixed slot.

  85%: four fields, the last a resizable address textarea. The read-only info
  sheet above is deliberately untouched — it has nothing to reach at its end.

  The unit is a PERCENTAGE of the app root, never vh/dvh: inside Telegram
  `html.telegram-webapp` sizes that root to the visible band while vh measures
  the whole WebView. tests/unit/styles/sheet-scroll-invariant.test.js enforces it.
*/
.shipping-edit-sheet {
    height: 85%;

    /* Framework7's .page-content folds this straight into its own padding-bottom
       (framework7-bundle.css:391), so the last field clears the sticky footer
       instead of hiding under it. Keep it in step with the footer's height in
       app.css: that is this var PLUS --f7-safe-area-bottom, and .page-content
       already adds that inset itself. */
    --sheet-fixed-footer-h: 76px;
    --f7-page-content-extra-padding-bottom: var(--sheet-fixed-footer-h);
}

.shipping-info-sheet .page-content,
.shipping-edit-sheet .page-content {
    background: var(--poolit-bg-soft);
}

.shipping-info-sheet .block-title,
.shipping-edit-sheet .block-title {
    color: var(--poolit-text-headline);
    font-weight: var(--poolit-font-weight-semibold);
    margin-top: var(--poolit-spacing-md);
}

.shipping-info-sheet .list,
.shipping-edit-sheet .list {
    margin-top: var(--poolit-spacing-sm);
    margin-bottom: var(--poolit-spacing-md);
}

/* Multiline item-after for address display */
.item-after-multiline {
    white-space: normal;
    text-align: end;
    max-width: 60%;
    line-height: 1.4;
}

/* Ensure phone input displays properly in LTR regardless of app direction */
:deep(.item-input-wrap input[type="tel"]) {
    direction: ltr !important;
    text-align: left !important;
}

/* Adjust clear button position for phone input */
:deep(.item-input-wrap) {
    position: relative;
}

:deep(.item-inner-end) {
    margin-inline-start: 8px;
}

@media (max-width: 374px) {
    .package-title {
        font-size: 18px;
    }

    .stat-value {
        font-size: 16px;
    }

    .stat-value-emphasized {
        font-size: 18px;
    }
}

@media (min-width: 768px) {
    .package-card {
        max-width: 600px;
        margin: 0 auto;
    }
}
</style>
